import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * MED-1073 (E1.3i / AC-LP1, AC-LP2) — reliable unsaved-changes leave-prompt.
 *
 * Why a hand-rolled guard instead of React Router's `useBlocker`: the app
 * mounts the legacy declarative `<BrowserRouter>` (see App.tsx), and in React
 * Router 7 `useBlocker` only works under a *data* router
 * (`createBrowserRouter` + `RouterProvider`). Migrating the whole app's router
 * is far out of scope for this UAT refinement, so we guard navigation at the
 * platform level instead. e1.3d (MED-975) intercepted only the editor's own
 * Cancel + back-arrow buttons; sidebar / in-app `<Link>` clicks slipped
 * through — that gap is the defect this fixes.
 *
 * Three vectors are covered while `when` is true:
 *   1. Browser tab close / refresh / external nav → native `beforeunload`.
 *   2. In-app `<Link>`/anchor navigation (sidebar, breadcrumbs, etc.) →
 *      capture-phase click interception that hands a `proceed` thunk to
 *      `onAttempt` (the page renders its confirm modal; "Leave" calls
 *      `proceed`, which performs the original navigation).
 *   3. Browser Back / Forward → a single pinned history sentinel + `popstate`
 *      re-pin, then the same `onAttempt` confirm flow.
 *
 * When `when` is false the guard is inert and ordinary navigation is never
 * intercepted (AC-LP2).
 */
export interface UnsavedChangesGuardOptions {
  /** Guard only while there are unsaved changes (e.g. `isDirty`). */
  when: boolean;
  /**
   * Called when an in-app navigation is intercepted. Receives a `proceed`
   * thunk the caller invokes once the user confirms they want to leave.
   */
  onAttempt: (proceed: () => void) => void;
}

export function useUnsavedChangesGuard({ when, onAttempt }: UnsavedChangesGuardOptions): void {
  const navigate = useNavigate();
  // Read the latest values inside long-lived listeners without re-subscribing.
  const whenRef = useRef(when);
  whenRef.current = when;
  const onAttemptRef = useRef(onAttempt);
  onAttemptRef.current = onAttempt;

  // 1) Browser-level navigation (refresh, tab close, external link). The
  //    browser shows its own native dialog — we only opt in by setting
  //    returnValue while dirty.
  useEffect(() => {
    if (!when) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [when]);

  // 2) In-app link navigation. React Router renders `<Link>` as an `<a>`, so a
  //    capture-phase document click listener can intercept the click before
  //    the router's own handler runs and reroute it through the confirm modal.
  useEffect(() => {
    if (!when) return;
    const handler = (e: MouseEvent) => {
      // Let the browser handle modified / non-primary clicks (open in new
      // tab, etc.) and anything already handled.
      if (
        e.defaultPrevented ||
        e.button !== 0 ||
        e.metaKey ||
        e.ctrlKey ||
        e.shiftKey ||
        e.altKey
      ) {
        return;
      }
      const anchor = (e.target as HTMLElement | null)?.closest('a');
      if (!anchor) return;

      const href = anchor.getAttribute('href');
      const target = anchor.getAttribute('target');
      // Skip non-navigations, downloads, hash-only, and new-tab links.
      if (!href || href.startsWith('#') || anchor.hasAttribute('download')) return;
      if (target && target !== '_self') return;

      const dest = new URL(href, window.location.href);
      // Only guard same-origin, single-page navigations. External origins
      // are full-page loads handled by `beforeunload` above.
      if (dest.origin !== window.location.origin) return;

      const to = dest.pathname + dest.search + dest.hash;
      const here = window.location.pathname + window.location.search + window.location.hash;
      if (to === here) return; // no real navigation

      e.preventDefault();
      e.stopPropagation();
      onAttemptRef.current(() => navigate(to));
    };
    // Capture phase so we win the race against React Router's listener.
    document.addEventListener('click', handler, true);
    return () => document.removeEventListener('click', handler, true);
  }, [when, navigate]);

  // 3) Browser Back / Forward. A single sentinel entry is pushed the first
  //    time the editor goes dirty; each Back press pops it, we re-pin to keep
  //    the user in place, then confirm. Pinning exactly once (via the ref
  //    flag) keeps the history stack from accumulating duplicate entries
  //    across save cycles, which would otherwise break the `go(-2)` jump.
  const pinnedRef = useRef(false);
  const leavingRef = useRef(false);
  useEffect(() => {
    const onPop = () => {
      if (leavingRef.current) return;
      if (!whenRef.current) return; // clean → allow the navigation
      // Re-pin so the user stays on the editor while we ask.
      window.history.pushState(null, '', window.location.href);
      onAttemptRef.current(() => {
        leavingRef.current = true;
        // Jump back past our sentinel and the editor entry to the prior page.
        window.history.go(-2);
      });
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  useEffect(() => {
    if (when && !pinnedRef.current) {
      window.history.pushState(null, '', window.location.href);
      pinnedRef.current = true;
    }
  }, [when]);
}
